import { useState, useEffect, useCallback, useRef } from 'react';

function getConnectionQuality(connection, isOnline) {
  if (!isOnline) return 'offline';

  if (connection) {
    const { effectiveType, rtt } = connection;
    if (effectiveType === '4g' && (rtt == null || rtt < 500)) return 'good';
    return 'slow';
  }

  // API unavailable but online — assume good
  return 'good';
}

export function useNetworkStatus() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [effectiveType, setEffectiveType] = useState(connection?.effectiveType ?? null);
  const [connectionQuality, setConnectionQuality] = useState(() =>
    getConnectionQuality(connection, navigator.onLine),
  );
  const [showWarning, setShowWarning] = useState(() => {
    const quality = getConnectionQuality(connection, navigator.onLine);
    return quality === 'slow' || quality === 'offline';
  });

  // Track the quality at the time warning was dismissed so we can re-show on further degradation
  const dismissedAtQualityRef = useRef(null);

  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    dismissedAtQualityRef.current = connectionQuality;
  }, [connectionQuality]);

  useEffect(() => {
    function update() {
      const online = navigator.onLine;
      const quality = getConnectionQuality(connection, online);

      setIsOnline(online);
      setEffectiveType(connection?.effectiveType ?? null);
      setConnectionQuality(quality);

      if (quality === 'good') {
        // Connection recovered — reset dismissed state so a future degradation re-shows
        setShowWarning(false);
        dismissedAtQualityRef.current = null;
      } else if (dismissedAtQualityRef.current === null) {
        // Not dismissed — show warning
        setShowWarning(true);
      } else if (
        quality === 'offline' &&
        dismissedAtQualityRef.current === 'slow'
      ) {
        // Degraded further since dismiss — re-show
        setShowWarning(true);
        dismissedAtQualityRef.current = null;
      }
    }

    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    connection?.addEventListener('change', update);

    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
      connection?.removeEventListener('change', update);
    };
  }, [connection]);

  return { isOnline, connectionQuality, effectiveType, showWarning, dismissWarning };
}
